import { motion, AnimatePresence } from 'framer-motion';
import { useMemo, useRef, useEffect, useState } from 'react';
import { DEFAULT_PRIMARY_COLOR } from '@/constants';
import { resolveTableHeaderColor } from '@/lib/table-utils';
import { getTextColorForBackground } from '@/utils/getTextColorForBackground';
import { resolvePreviewUrl } from '@/utils/preview-cache';
import type { ActiveSection, ColorTarget } from '../types';
import type {
    HeaderCustomizations,
    FooterCustomizations,
    TableCustomizations,
    DividerCustomizations,
} from '@/components/preview/types';
import type { CSSProperties } from 'react';

interface SkeletonPreviewProps {
    activeSection: ActiveSection;
    /** Row focused inside Cores; decides which page the colour lands on. */
    colorTarget: ColorTarget;
    headerConfig: HeaderCustomizations;
    footerConfig: FooterCustomizations;
    tableConfig: TableCustomizations;
    dividerConfig: DividerCustomizations;
    primaryColor: string | undefined;
    secondaryColor: string | undefined;
    fontFamily: string;
    psychologistName: string;
    psychologistCrp: string | undefined;
}

// ---------------------------------------------------------------------------
// Skeleton bar: a rounded gray rectangle that mimics a line of text
// ---------------------------------------------------------------------------
function SkeletonBar({
    width = '100%',
    height = 10,
    className = '',
    style,
}: {
    width?: string | number;
    height?: number;
    className?: string;
    style?: CSSProperties;
}) {
    return (
        <div
            className={`rounded-sm bg-muted ${className}`}
            style={{ width, height, flexShrink: 0, ...style }}
        />
    );
}

// ---------------------------------------------------------------------------
// Subtle active-zone highlight (replaces the old FocusRing)
// A very faint blue wash + thin left accent border when the zone is active.
// ---------------------------------------------------------------------------
function zoneClass(isActive: boolean): string {
    return isActive
        ? 'bg-blue-50/40 border-l-2 border-l-blue-400/60 transition-colors duration-200'
        : 'border-l-2 border-l-transparent transition-colors duration-200';
}

// ---------------------------------------------------------------------------
// Crossfade animation variants for page view transitions
// ---------------------------------------------------------------------------
const PAGE_TRANSITION = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
    transition: { duration: 0.2 },
};

// ---------------------------------------------------------------------------
// Zone Components — each mimics a region of the real report
// ---------------------------------------------------------------------------

function HeaderZone({
    config,
    isActive,
    psychologistName,
    psychologistCrp,
}: {
    config: HeaderCustomizations;
    isActive: boolean;
    psychologistName: string;
    psychologistCrp: string | undefined;
}) {
    const alignment = config.alignment || 'left';
    const alignItems =
        alignment === 'center' ? 'center' : alignment === 'right' ? 'flex-end' : 'flex-start';
    const textAlign =
        alignment === 'center'
            ? ('center' as const)
            : alignment === 'right'
              ? ('right' as const)
              : ('left' as const);

    const logoUrl = resolvePreviewUrl(config.logo_url);
    const logoSize = config.logo_size || 'medium';
    const logoHeight = logoSize === 'small' ? 28 : logoSize === 'large' ? 56 : 40;

    const descriptionValue = config.description?.value || '';

    return (
        <div className={`px-10 pb-3 pt-6 ${zoneClass(isActive)}`}>
            <div className="flex flex-col gap-1.5" style={{ alignItems }}>
                {config.show_logo && (
                    <div
                        className="flex items-center justify-center overflow-hidden rounded border border-border bg-muted"
                        style={{
                            height: logoHeight,
                            width: logoHeight * 2.2,
                        }}
                    >
                        {logoUrl ? (
                            <img src={logoUrl} alt="Logo" className="h-full object-contain" />
                        ) : (
                            <span className="text-[8px] text-muted-foreground">Logo</span>
                        )}
                    </div>
                )}

                {/* Psychologist name — show actual name */}
                {config.show_psychologist_name !== false && (
                    <span
                        className="text-[11px] font-bold leading-tight text-foreground"
                        style={{ textAlign }}
                    >
                        {psychologistName || 'Nome do Psicólogo'}
                    </span>
                )}

                {/* Description — live text when available, skeleton when empty */}
                {config.description?.showing &&
                    (descriptionValue ? (
                        <span
                            className="max-w-[220px] truncate text-[8px] leading-tight text-muted-foreground"
                            style={{ textAlign }}
                        >
                            {descriptionValue}
                        </span>
                    ) : (
                        <SkeletonBar width={180} height={8} className="bg-muted" />
                    ))}

                {/* CRP — show actual CRP when available */}
                {config.show_crp_number !== false &&
                    (psychologistCrp ? (
                        <span
                            className="text-[7px] leading-tight text-muted-foreground"
                            style={{ textAlign }}
                        >
                            CRP {psychologistCrp}
                        </span>
                    ) : (
                        <SkeletonBar width={72} height={7} className="bg-muted" />
                    ))}
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Patient info table — decorative skeleton of the real PatientInfoTable
// This is NOT affected by TableCustomizations; it's purely visual context.
// ---------------------------------------------------------------------------
function PatientInfoZone() {
    return (
        <div className="px-10 pb-3">
            <table className="w-full border-collapse text-[7px]">
                <tbody>
                    {/* Row 1: Nome | Sexo | Idade */}
                    <tr>
                        <td className="w-[18%] border border-border-strong bg-surface px-1.5 py-1 font-bold">
                            Nome:
                        </td>
                        <td className="border border-border-strong px-1.5 py-1">
                            <SkeletonBar width={60} height={5} className="bg-muted" />
                        </td>
                        <td className="w-[14%] border border-border-strong bg-surface px-1.5 py-1 font-bold">
                            Sexo:
                        </td>
                        <td className="border border-border-strong px-1.5 py-1">
                            <SkeletonBar width={30} height={5} className="bg-muted" />
                        </td>
                        <td className="w-[14%] border border-border-strong bg-surface px-1.5 py-1 font-bold">
                            Idade:
                        </td>
                        <td className="border border-border-strong px-1.5 py-1">
                            <SkeletonBar width={20} height={5} className="bg-muted" />
                        </td>
                    </tr>
                    {/* Row 2: Data Nasc | Escolaridade */}
                    <tr>
                        <td className="border border-border-strong bg-surface px-1.5 py-1 font-bold">
                            Data Nasc:
                        </td>
                        <td className="border border-border-strong px-1.5 py-1">
                            <SkeletonBar width={48} height={5} className="bg-muted" />
                        </td>
                        <td
                            className="border border-border-strong bg-surface px-1.5 py-1 font-bold"
                            colSpan={2}
                        >
                            Escolaridade:
                        </td>
                        <td className="border border-border-strong px-1.5 py-1" colSpan={2}>
                            <SkeletonBar width={56} height={5} className="bg-muted" />
                        </td>
                    </tr>
                    {/* Row 3: Solicitante */}
                    <tr>
                        <td className="border border-border-strong bg-surface px-1.5 py-1 font-bold">
                            Solicitante:
                        </td>
                        <td className="border border-border-strong px-1.5 py-1" colSpan={5}>
                            <SkeletonBar width={120} height={5} className="bg-muted" />
                        </td>
                    </tr>
                    {/* Row 4: Finalidade */}
                    <tr>
                        <td className="border border-border-strong bg-surface px-1.5 py-1 font-bold">
                            Finalidade:
                        </td>
                        <td className="border border-border-strong px-1.5 py-1" colSpan={5}>
                            <SkeletonBar width={140} height={5} className="bg-muted" />
                        </td>
                    </tr>
                    {/* Row 5: Período de Avaliação */}
                    <tr>
                        <td className="whitespace-nowrap border border-border-strong bg-surface px-1.5 py-1 font-bold">
                            Período:
                        </td>
                        <td className="border border-border-strong px-1.5 py-1" colSpan={5}>
                            <div className="flex items-center gap-1">
                                <SkeletonBar width={44} height={5} className="bg-muted" />
                                <span className="shrink-0 text-[6px] text-muted-foreground">
                                    até
                                </span>
                                <SkeletonBar width={44} height={5} className="bg-muted" />
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Test results table — matches the real TestTable structure
// Columns: Teste | Resultado Bruto | Classificação
// ---------------------------------------------------------------------------

const TEST_TABLE_MOCK_DATA = [
    ['WISC-V', '85', 'Médio'],
    ['RAVLT', '42', 'Médio Inferior'],
    ['Figuras de Rey', '28', 'Adequado'],
];

const TEST_TABLE_HEADERS = ['Teste', 'Resultado Bruto', 'Classificação'];

function TestTableZone({
    config,
    primaryColor,
}: {
    config: TableCustomizations;
    primaryColor: string;
}) {
    const headerColor = resolveTableHeaderColor(config.header_color, primaryColor);
    const headerTextColor = getTextColorForBackground(headerColor);
    const textAlign = (config.text_alignment || 'center') as CSSProperties['textAlign'];

    return (
        <div className="px-10 pb-3">
            <table className="w-full border-collapse text-[7px]">
                <colgroup>
                    {/* Proportional to real defaults: 200, 100, 100 */}
                    <col style={{ width: '50%' }} />
                    <col style={{ width: '25%' }} />
                    <col style={{ width: '25%' }} />
                </colgroup>
                <thead>
                    <tr>
                        {TEST_TABLE_HEADERS.map((header) => (
                            <th
                                key={header}
                                className="border border-border-strong px-1.5 py-1 font-semibold"
                                style={{
                                    backgroundColor: headerColor,
                                    color: headerTextColor,
                                    textAlign,
                                }}
                            >
                                {header}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {TEST_TABLE_MOCK_DATA.map((row, rowIndex) => (
                        <tr
                            key={rowIndex}
                            className={rowIndex % 2 === 0 ? 'bg-white' : 'bg-surface'}
                        >
                            {row.map((cell, colIndex) => (
                                <td
                                    key={colIndex}
                                    className="border border-border-strong px-1.5 py-1 text-muted-foreground"
                                    style={{ textAlign }}
                                >
                                    {cell}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Divider block — the "group title" the report paints between test blocks.
// It shows up inline on the full page and again on its own view, so it lives
// here once. The `background_color` sentinel resolves exactly as it does in
// `TestDivider.tsx`: 'primary' means the report colour, empty means
// transparent, anything else is a literal hex.
// ---------------------------------------------------------------------------

/** Real words, not a skeleton bar — bold and italic are invisible on a grey rectangle. */
const DIVIDER_SAMPLE_TITLE = 'Inteligência';

function DividerBlockPreview({
    dividerConfig,
    primaryColor,
    className,
}: {
    dividerConfig: DividerCustomizations;
    primaryColor: string;
    className?: string;
}) {
    const alignment = dividerConfig.alignment || 'center';
    const showLine = dividerConfig.show_line !== false;
    const fontStyle = dividerConfig.font_style || 'normal';
    const rawBgColor = dividerConfig.background_color ?? 'primary';
    const resolvedBgColor = rawBgColor === 'primary' ? primaryColor : rawBgColor || undefined;
    const textColor = dividerConfig.font_color
        ? dividerConfig.font_color
        : resolvedBgColor
          ? getTextColorForBackground(resolvedBgColor)
          : undefined;

    const containerStyle: CSSProperties = resolvedBgColor
        ? {
              backgroundColor: resolvedBgColor,
              padding: '4px 6px',
              borderRadius: '3px',
          }
        : {};

    const titleStyle: CSSProperties = {
        textAlign: alignment,
        fontWeight: fontStyle === 'bold' ? 'bold' : 'normal',
        fontStyle: fontStyle === 'italic' ? 'italic' : 'normal',
        // #334155 is the slate-700 the real renderer falls back to.
        color: textColor || '#334155',
    };

    // 9px rather than the ~7px the page scale would give: at 7px bold and
    // normal are the same shape.
    return (
        <div className={className} style={containerStyle}>
            <div
                className="text-[9px] leading-tight"
                style={titleStyle}
                data-testid="preview-divider-title"
            >
                {DIVIDER_SAMPLE_TITLE}
            </div>
            {showLine && <hr className="mt-0.5 border-t" style={{ borderColor: primaryColor }} />}
        </div>
    );
}

// ---------------------------------------------------------------------------
// Section zone — represents a report section with numbered title and content
// ---------------------------------------------------------------------------
function SectionZone({
    number,
    primaryColor,
    dividerConfig,
    isActive,
    showDivider,
}: {
    number: number;
    primaryColor: string;
    dividerConfig: DividerCustomizations;
    isActive: boolean;
    showDivider?: boolean;
}) {
    return (
        <div className={`px-10 pb-2 ${zoneClass(isActive)}`}>
            {/* Section Title */}
            <div className="mb-1 flex items-center gap-1.5">
                <span className="text-[10px] font-bold" style={{ color: primaryColor }}>
                    {number}.
                </span>
                <SkeletonBar width={90} height={8} />
            </div>
            <hr className="mb-2 border-t" style={{ borderColor: primaryColor }} />

            {/* Divider block (only in one section to show the style) */}
            {showDivider && (
                <DividerBlockPreview
                    dividerConfig={dividerConfig}
                    primaryColor={primaryColor}
                    className="mb-2"
                />
            )}

            {/* Content skeleton lines */}
            <div className="space-y-1">
                <SkeletonBar width="100%" height={6} className="bg-muted" />
                <SkeletonBar width="100%" height={6} className="bg-muted" />
                <SkeletonBar width="75%" height={6} className="bg-muted" />
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Signature zone
// ---------------------------------------------------------------------------
function SignatureZone() {
    return (
        <div className="px-10 pt-3">
            <div className="flex flex-col items-end gap-1 border-t border-border-strong pt-2">
                <SkeletonBar width={60} height={5} className="bg-muted" />
                <SkeletonBar width={120} height={1} className="bg-border-strong" />
                <SkeletonBar width={80} height={5} className="bg-muted" />
                <SkeletonBar width={48} height={5} className="bg-muted" />
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Footer zone
// ---------------------------------------------------------------------------
function FooterZone({
    config,
    primaryColor,
    isActive,
}: {
    config: FooterCustomizations;
    primaryColor: string;
    isActive: boolean;
}) {
    const padding = config.padding || 12;
    const textColor = getTextColorForBackground(primaryColor);

    // Count visible items to distribute space
    const items: string[] = [];
    if (config.phone_number_visible) items.push('phone');
    if (config.psychologist_name_visible) items.push('name');
    if (config.email_visible) items.push('email');
    if (config.instagram_visible) items.push('instagram');

    return (
        <div className={`mt-auto ${zoneClass(isActive)}`}>
            {/* Page number */}
            <div className="py-1 text-center">
                <span className="text-[6px] text-muted-foreground">Página 1 de 1</span>
            </div>
            {/* Footer bar */}
            <div
                className="px-6"
                style={{
                    backgroundColor: primaryColor,
                    paddingTop: `${padding * 0.5}px`,
                    paddingBottom: `${padding * 0.5}px`,
                }}
            >
                <div className="flex items-center justify-between gap-2">
                    {items.map((item) => (
                        <SkeletonBar
                            key={item}
                            width={item === 'name' ? 60 : 44}
                            height={5}
                            style={{ backgroundColor: `${textColor}40` }}
                        />
                    ))}
                    {items.length === 0 && <div className="h-[5px]" />}
                </div>
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Letterhead zone
// ---------------------------------------------------------------------------
function LetterheadZone({ letterheadUrl }: { letterheadUrl?: string }) {
    const url = resolvePreviewUrl(letterheadUrl);
    return (
        <div className="flex h-12 w-full items-center justify-center overflow-hidden border-b border-border bg-surface">
            {url ? (
                <img src={url} alt="Papel timbrado" className="size-full object-cover" />
            ) : (
                <span className="text-[8px] text-muted-foreground">Papel Timbrado</span>
            )}
        </div>
    );
}

// ---------------------------------------------------------------------------
// Report Skeleton View — the default full-report preview
// ---------------------------------------------------------------------------
function ReportSkeletonContent({
    headerConfig,
    footerConfig,
    dividerConfig,
    primaryColor,
    activeSection,
    psychologistName,
    psychologistCrp,
}: {
    headerConfig: HeaderCustomizations;
    footerConfig: FooterCustomizations;
    dividerConfig: DividerCustomizations;
    primaryColor: string;
    activeSection: ActiveSection;
    psychologistName: string;
    psychologistCrp: string | undefined;
}) {
    return (
        <motion.div key="report" className="flex h-full flex-col" {...PAGE_TRANSITION}>
            {/* Letterhead zone */}
            {headerConfig.show_letterhead && (
                <LetterheadZone letterheadUrl={headerConfig.letterhead_url} />
            )}

            <HeaderZone
                config={headerConfig}
                isActive={activeSection === 'header' || activeSection === 'brand'}
                psychologistName={psychologistName}
                psychologistCrp={psychologistCrp}
            />

            <PatientInfoZone />

            {/* Sections — the body is the only elastic zone: it absorbs the slack
                when the header is lean and clips from the bottom when it is not,
                so the signature and the footer always stay on the page. */}
            <div className="min-h-0 flex-1 overflow-hidden">
                <SectionZone
                    number={1}
                    primaryColor={primaryColor}
                    dividerConfig={dividerConfig}
                    isActive={false}
                />
                <SectionZone
                    number={2}
                    primaryColor={primaryColor}
                    dividerConfig={dividerConfig}
                    isActive={activeSection === 'divider'}
                    showDivider
                />
                <SectionZone
                    number={3}
                    primaryColor={primaryColor}
                    dividerConfig={dividerConfig}
                    isActive={false}
                />
            </div>

            <SignatureZone />

            <FooterZone
                config={footerConfig}
                primaryColor={primaryColor}
                isActive={activeSection === 'footer'}
            />
        </motion.div>
    );
}

// ---------------------------------------------------------------------------
// Font Preview View — dedicated page layout for font selection
// Shows "LaudoPsi" in the active font with section-style context.
// ---------------------------------------------------------------------------
function FontPreviewContent({
    primaryColor,
    footerConfig,
    activeSection,
}: {
    primaryColor: string;
    footerConfig: FooterCustomizations;
    activeSection: ActiveSection;
}) {
    return (
        <motion.div key="font-preview" className="flex h-full flex-col" {...PAGE_TRANSITION}>
            {/* Section heading */}
            <div className={`px-10 pb-2 pt-8 ${zoneClass(true)}`}>
                <div className="mb-1 flex items-center gap-1.5">
                    <span className="text-[10px] font-bold" style={{ color: primaryColor }}>
                        1.
                    </span>
                    <span className="text-[10px] font-bold text-muted-foreground">Introdução</span>
                </div>
                <hr className="mb-3 border-t" style={{ borderColor: primaryColor }} />

                {/* Context skeleton lines */}
                <div className="mb-4 space-y-1">
                    <SkeletonBar width="100%" height={6} className="bg-muted" />
                    <SkeletonBar width="90%" height={6} className="bg-muted" />
                </div>

                {/* Prominent font showcase */}
                <div className="flex flex-col items-center gap-2 py-3">
                    <span className="text-[22px] font-semibold tracking-wide text-foreground">
                        LaudoPsi
                    </span>
                    <span className="text-[10px] text-muted-foreground">&lt;3</span>
                </div>

                {/* More context skeleton lines */}
                <div className="mt-4 space-y-1">
                    <SkeletonBar width="100%" height={6} className="bg-muted" />
                    <SkeletonBar width="100%" height={6} className="bg-muted" />
                    <SkeletonBar width="65%" height={6} className="bg-muted" />
                </div>
            </div>

            {/* Spacer to push footer to bottom */}
            <div className="flex-1" />

            <FooterZone
                config={footerConfig}
                primaryColor={primaryColor}
                isActive={activeSection === 'footer'}
            />
        </motion.div>
    );
}

// ---------------------------------------------------------------------------
// Divider Preview View — dedicated layout for divider config
// Shows skeleton text above, the styled divider, and skeleton text below.
// ---------------------------------------------------------------------------
function DividerContent({
    dividerConfig,
    footerConfig,
    primaryColor,
    activeSection,
}: {
    dividerConfig: DividerCustomizations;
    footerConfig: FooterCustomizations;
    primaryColor: string;
    activeSection: ActiveSection;
}) {
    return (
        <motion.div key="divider-preview" className="flex h-full flex-col" {...PAGE_TRANSITION}>
            <div className={`px-10 pb-2 pt-8 ${zoneClass(true)}`}>
                {/* Skeleton text above the divider */}
                <div className="mb-3 space-y-1">
                    <SkeletonBar width="100%" height={6} className="bg-muted" />
                    <SkeletonBar width="100%" height={6} className="bg-muted" />
                    <SkeletonBar width="85%" height={6} className="bg-muted" />
                </div>

                {/* The divider block */}
                <DividerBlockPreview
                    dividerConfig={dividerConfig}
                    primaryColor={primaryColor}
                    className="mb-3"
                />

                {/* Skeleton text below the divider */}
                <div className="space-y-1">
                    <SkeletonBar width="100%" height={6} className="bg-muted" />
                    <SkeletonBar width="100%" height={6} className="bg-muted" />
                    <SkeletonBar width="60%" height={6} className="bg-muted" />
                </div>
            </div>

            {/* Spacer to push footer to bottom */}
            <div className="flex-1" />

            <FooterZone
                config={footerConfig}
                primaryColor={primaryColor}
                isActive={activeSection === 'footer'}
            />
        </motion.div>
    );
}

// ---------------------------------------------------------------------------
// Test Table View — dedicated full-page layout for table config
// ---------------------------------------------------------------------------
function TestTableContent({
    tableConfig,
    footerConfig,
    primaryColor,
    activeSection,
}: {
    tableConfig: TableCustomizations;
    footerConfig: FooterCustomizations;
    primaryColor: string;
    activeSection: ActiveSection;
}) {
    return (
        <motion.div key="test-table" className="flex h-full flex-col" {...PAGE_TRANSITION}>
            {/* Section-style heading for context */}
            <div className={`px-10 pb-2 pt-8 ${zoneClass(true)}`}>
                <div className="mb-1 flex items-center gap-1.5">
                    <span className="text-[10px] font-bold" style={{ color: primaryColor }}>
                        3.
                    </span>
                    <span className="text-[10px] font-bold text-muted-foreground">Resultados</span>
                </div>
                <hr className="mb-2 border-t" style={{ borderColor: primaryColor }} />

                {/* Context skeleton lines before the table */}
                <div className="mb-3 space-y-1">
                    <SkeletonBar width="100%" height={6} className="bg-muted" />
                    <SkeletonBar width="85%" height={6} className="bg-muted" />
                </div>
            </div>

            {/* The test results table */}
            <div className={zoneClass(true)}>
                <TestTableZone config={tableConfig} primaryColor={primaryColor} />
            </div>

            {/* Context skeleton lines after the table */}
            <div className={`px-10 pb-3 pt-2 ${zoneClass(true)}`}>
                <div className="space-y-1">
                    <SkeletonBar width="100%" height={6} className="bg-muted" />
                    <SkeletonBar width="100%" height={6} className="bg-muted" />
                    <SkeletonBar width="60%" height={6} className="bg-muted" />
                </div>
            </div>

            {/* Spacer to push footer to bottom */}
            <div className="flex-1" />

            <FooterZone
                config={footerConfig}
                primaryColor={primaryColor}
                isActive={activeSection === 'footer'}
            />
        </motion.div>
    );
}

const PAGE_HEIGHT = 509;
const PAGE_WIDTH = 360;
const FILL_RATIO = 0.9;
const MIN_BASE_SCALE = 0.7;
const MAX_BASE_SCALE = 1.3;
const FALLBACK_BASE_SCALE = 1.1;

// ---------------------------------------------------------------------------
// Zoom transform configs per active section
// scaleMultiplier multiplies the dynamic baseScale. focusY is the point of the
// page — in unscaled page coordinates, 0 at the top edge and PAGE_HEIGHT at the
// bottom — the zoom tries to keep centered in the viewport. The pan is always
// clamped to the page edges (see `zoom` below), so a focus near an edge simply
// aligns that edge instead of scrolling past it into empty space.
// ---------------------------------------------------------------------------
const ZOOM_CONFIGS: Record<
    NonNullable<ActiveSection>,
    { scaleMultiplier: number; focusY: number }
> = {
    header: { scaleMultiplier: 1.15, focusY: 110 },
    brand: { scaleMultiplier: 1.15, focusY: 110 },
    table: { scaleMultiplier: 1.1, focusY: 220 },
    divider: { scaleMultiplier: 1.1, focusY: 220 },
    footer: { scaleMultiplier: 1.15, focusY: PAGE_HEIGHT - 40 },
    font: { scaleMultiplier: 1.1, focusY: 220 },
    colors: { scaleMultiplier: 1.0, focusY: PAGE_HEIGHT / 2 },
};

const DEFAULT_ZOOM = { scaleMultiplier: 1.0, focusY: PAGE_HEIGHT / 2 };

const SPRING_TRANSITION = {
    type: 'spring' as const,
    stiffness: 260,
    damping: 28,
    mass: 0.8,
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export function SkeletonPreview({
    activeSection,
    colorTarget,
    headerConfig,
    footerConfig,
    tableConfig,
    dividerConfig,
    primaryColor: rawPrimary,
    secondaryColor: _secondaryColor,
    fontFamily,
    psychologistName,
    psychologistCrp,
}: SkeletonPreviewProps) {
    const primaryColor = rawPrimary || DEFAULT_PRIMARY_COLOR;
    const containerRef = useRef<HTMLDivElement>(null);

    // Which page to show is not the same question as which accordion is open: the
    // table and divider colours now live inside Cores, so focusing one of those
    // rows has to bring its element onto the page or the colour is picked blind.
    const previewSection: ActiveSection =
        activeSection === 'colors' && colorTarget ? colorTarget : activeSection;

    const isTableView = previewSection === 'table';
    const isDividerView = previewSection === 'divider';
    const isFontView = previewSection === 'font';

    // -----------------------------------------------------------------------
    // Vertically responsive: track container height via ResizeObserver
    // -----------------------------------------------------------------------
    const [containerHeight, setContainerHeight] = useState(0);

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;

        const observer = new ResizeObserver((entries) => {
            for (const entry of entries) {
                setContainerHeight(entry.contentRect.height);
            }
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    // Compute a dynamic base scale that fits the A4 page into the container
    const baseScale = useMemo(() => {
        if (containerHeight <= 0) return FALLBACK_BASE_SCALE;
        const raw = (containerHeight * FILL_RATIO) / PAGE_HEIGHT;
        return Math.min(Math.max(raw, MIN_BASE_SCALE), MAX_BASE_SCALE);
    }, [containerHeight]);

    // Compute the final zoom transform from baseScale × per-section multiplier.
    // The layout box keeps its unscaled PAGE_HEIGHT and sits centered in the
    // container, so with transformOrigin 'top center' a page point `p` renders
    // at `layoutTop + p * scale + translateY`. Solving that for "focusY lands on
    // the container's center" gives the pan below; `centered` is the same
    // formula with focusY at the middle of the page.
    const zoom = useMemo(() => {
        const config = previewSection
            ? (ZOOM_CONFIGS[previewSection] ?? DEFAULT_ZOOM)
            : DEFAULT_ZOOM;
        const scale = baseScale * config.scaleMultiplier;
        const scaledHeight = PAGE_HEIGHT * scale;
        const centered = (PAGE_HEIGHT - scaledHeight) / 2;

        // Page fits entirely: nothing to pan towards, just center it.
        if (containerHeight <= 0 || scaledHeight <= containerHeight) {
            return { scale, translateY: centered };
        }

        // Clamp the pan to the page edges so zooming never reveals empty space
        // above the top or below the bottom of the sheet.
        const minTranslateY = (containerHeight + PAGE_HEIGHT) / 2 - scaledHeight;
        const maxTranslateY = (PAGE_HEIGHT - containerHeight) / 2;
        const focused = PAGE_HEIGHT / 2 - config.focusY * scale;
        return { scale, translateY: Math.min(Math.max(focused, minTranslateY), maxTranslateY) };
    }, [previewSection, baseScale, containerHeight]);

    // Scroll the container into view when section changes
    useEffect(() => {
        containerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    }, [previewSection]);

    return (
        <div
            ref={containerRef}
            className="flex flex-1 items-center justify-center overflow-hidden bg-muted p-4"
        >
            <motion.div
                animate={{
                    scale: zoom.scale,
                    y: zoom.translateY,
                }}
                transition={SPRING_TRANSITION}
                style={{ transformOrigin: 'top center' }}
            >
                {/* A4-shaped page */}
                <div
                    className="report-paper relative overflow-hidden bg-white shadow-xl"
                    data-preview-view={previewSection ?? 'report'}
                    style={{
                        width: PAGE_WIDTH,
                        height: PAGE_HEIGHT,
                        fontFamily,
                        fontSize: 10,
                    }}
                >
                    <AnimatePresence mode="wait">
                        {isTableView ? (
                            <TestTableContent
                                tableConfig={tableConfig}
                                footerConfig={footerConfig}
                                primaryColor={primaryColor}
                                activeSection={previewSection}
                            />
                        ) : isDividerView ? (
                            <DividerContent
                                dividerConfig={dividerConfig}
                                footerConfig={footerConfig}
                                primaryColor={primaryColor}
                                activeSection={previewSection}
                            />
                        ) : isFontView ? (
                            <FontPreviewContent
                                primaryColor={primaryColor}
                                footerConfig={footerConfig}
                                activeSection={previewSection}
                            />
                        ) : (
                            <ReportSkeletonContent
                                headerConfig={headerConfig}
                                footerConfig={footerConfig}
                                dividerConfig={dividerConfig}
                                primaryColor={primaryColor}
                                activeSection={previewSection}
                                psychologistName={psychologistName}
                                psychologistCrp={psychologistCrp}
                            />
                        )}
                    </AnimatePresence>
                </div>
            </motion.div>
        </div>
    );
}
